import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { TiltCard, MagneticButton } from './InteractiveEffects';

const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const reasons = [
    {
      title: 'Affordable Pricing',
      description: 'Competitive rates without compromising on quality. We offer transparent pricing with no hidden costs.',
    },
    {
      title: 'Fast Delivery',
      description: 'Efficient project management ensures timely completion. We respect your deadlines and deliver on time.',
    },
    {
      title: 'High-Quality Materials',
      description: 'We use only premium-grade aluminium and glass sourced from trusted international suppliers.',
    },
    {
      title: 'Experienced Team',
      description: 'Our skilled technicians bring years of expertise in aluminium, glass, and maintenance works.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section 
      className="py-24 md:py-32 px-6 relative overflow-hidden"
      data-testid="why-choose-us-section"
      ref={ref}
    >
      {/* Glassy background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-blue-50/60 to-cyan-50/50"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <motion.span 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-sky-600 block mb-4 flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="w-8 h-px bg-gradient-to-r from-sky-500 to-cyan-400"></span>
              Why Work With Us
            </motion.span>
            <motion.h2 
              className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-800 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Why Choose <br/>
              <span className="text-gradient">Glow Space?</span>
            </motion.h2>
            <motion.p 
              className="text-slate-600 text-lg leading-relaxed mb-8"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              We combine Canadian quality standards with UAE market expertise to deliver exceptional results for every project. Our commitment to excellence sets us apart from the competition.
            </motion.p>

            <MagneticButton>
              <motion.a
                href="https://wa.me/971566832840?text=Hello%2C%20I%27m%20interested%20in%20your%20services"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex group"
                data-testid="why-choose-us-cta"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 }}
                whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(3, 105, 161, 0.3)' }}
                whileTap={{ scale: 0.98 }}
              >
                Get a Free Quote
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
            </MagneticButton>
          </motion.div>

          {/* Right - Reasons Grid with Tilt Effect */}
          <motion.div 
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {reasons.map((reason, index) => (
              <TiltCard key={reason.title} intensity={5}>
                <motion.div
                  className="flex gap-4 p-6 glass-card rounded-2xl group cursor-pointer relative overflow-hidden"
                  data-testid={`reason-${index}`}
                  variants={itemVariants}
                >
                  {/* Progress line */}
                  <motion.div
                    className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#25D366] to-emerald-400 rounded-full"
                    initial={{ scaleY: 0 }}
                    whileHover={{ scaleY: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ originY: 0 }}
                />
                
                <motion.div 
                  className="flex-shrink-0"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <CheckCircle2 size={24} className="text-[#25D366]" />
                </motion.div>
                <div>
                  <h3 
                    className="text-lg font-medium text-slate-800 mb-2 group-hover:text-slate-900 transition-colors"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {reason.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed group-hover:text-slate-700 transition-colors">
                    {reason.description}
                  </p>
                </div>
              </motion.div>
              </TiltCard>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
